import type { Callable } from '@snowit/types/shared';
import type PERMISSIONS from '@snowit/utilities/permissions';
import type { DatabaseName, OnFirebaseErrorFn } from './types';

import useModals from '@snowit/hooks/useModals';
import useNotifications from '@snowit/hooks/useNotifications';
import { Unicodes } from '@snowit/iceconst';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';

import FirebaseSDKFactory from './FirebaseSDKFactory';

export type CallableParams = {
  permissions?: PERMISSIONS[];
  message?: string;
  blockNotify?: boolean;
};

const useFirebase = (name: DatabaseName = 'default') => {
  const { showErrorV2 } = useModals();
  const { t, te } = useLanguage();
  const { hasEveryPermission } = useAuth();
  const notify = useNotifications();
  // const hasEveryPermission = (..._any: any[]) => true;

  const displayError: OnFirebaseErrorFn = (error, action, collectionName, subcollectionName) => {
    const code = ['firebase', action, collectionName, subcollectionName].filter(Boolean).join('/');
    const msg = t(`lzy.error.firebase_${action}`, {}, 'lzy.error.firebase');
    showErrorV2({
      code,
      message: [
        msg,
        // error?.code ? te(`firebase/${error?.code}`) : undefined,
        t(te(error?.code), {}, error?.message),
      ]
        .filter((it): it is string => !!it && typeof it === 'string')
        .join('\n\n\n'),
      stack: false,
    });
  };

  const firebase = FirebaseSDKFactory(name, displayError);

  const sendOkNotification = (name: string, params: CallableParams) => {
    if (params.blockNotify) return;
    const [ns, ...action] = name.split('-');
    const title: string | undefined = [
      params.message ? t('lzy.notify.success') : undefined,
      action.join('.') || ns,
    ]
      .filter(Boolean)
      .join(Unicodes.BULLET);
    const message: string = params.message || t('lzy.notify.success');
    notify({ theme: 'ok', message, title, duration: 4000 });
  };

  const call = async <T extends Record<string, any>, K extends any = any>(
    name: string,
    data: T = {} as any,
    params: CallableParams = {},
  ): Promise<Callable<K>> => {
    try {
      await firebase.getToken();
      if (params.permissions && !hasEveryPermission(...params.permissions)) {
        const _error: any = new Error('Permissions check failed');
        _error.code = 'auth/permissions-check-failed';
        throw _error;
      }
      const callable = await firebase.httpsCallable(name);
      const response = await callable(data);
      sendOkNotification(name, params);
      return response as Callable<K>;
    } catch (error: any) {
      console.error(name, {
        error,
        data,
        permissions: params.permissions,
      });
      showErrorV2({
        code: error?.details?.code || error?.code,
        message: error?.details?.message || error?.message,
        stack: true,
      });
      throw error;
    }
  };

  return {
    ...firebase,
    call,
  };
};

export default useFirebase;
